/**
 * NexusConfigProvider - Feature flags and configuration
 * Based on ai-retail TenantConfigProvider pattern
 */

'use client'

import { createContext, useContext, useMemo, ReactNode } from 'react'
import { useCurrentAccount, type ViewPermission } from '@repo/api-client'
import { useViewPermissions } from '@/stores'
import { MODULE_PERMISSION_GROUPS } from '@/shared/config/view-permissions'

/**
 * Feature flags configuration
 */
export interface NexusFeatures {
  // Communication features
  voiceCalls: boolean
  videoCalls: boolean
  conferencing: boolean
  screenSharing: boolean

  // Chat features
  chatForwarding: boolean
  voiceMessages: boolean
  fileSharing: boolean
  groupChats: boolean
  channels: boolean

  // Advanced features
  endToEndEncryption: boolean
  messageScheduling: boolean
  polls: boolean
  reactions: boolean
}

/**
 * Module availability configuration
 */
export interface NexusModules {
  chat: boolean
  calls: boolean
  contacts: boolean
  conference: boolean
  crm: boolean
  hr: boolean
  documents: boolean
  timesheet: boolean
  wallet: boolean
  billing: boolean
  media: boolean
}

/**
 * Complete Nexus configuration
 */
export interface NexusConfig {
  /**
   * Feature flags
   */
  features: NexusFeatures

  /**
   * Module availability
   */
  modules: NexusModules

  /**
   * User permissions (from viewPermissions)
   */
  permissions: string[]

  /**
   * Check if a specific feature is enabled
   */
  isFeatureEnabled: (feature: keyof NexusFeatures) => boolean

  /**
   * Check if a specific module is enabled
   */
  isModuleEnabled: (module: keyof NexusModules) => boolean

  /**
   * Check if user has a specific permission
   */
  hasPermission: (permission: string) => boolean

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission: (permissions: string[]) => boolean

  /**
   * Check if user has all of the specified permissions
   */
  hasAllPermissions: (permissions: string[]) => boolean
}

const NexusConfigContext = createContext<NexusConfig | null>(null)

interface NexusConfigProviderProps {
  children: ReactNode
  /**
   * Override feature flags (for testing or tenant-specific configs)
   */
  featureOverrides?: Partial<NexusFeatures>
  /**
   * Override module availability (for testing or tenant-specific configs)
   */
  moduleOverrides?: Partial<NexusModules>
}

/**
 * Build feature flags from account configuration
 */
function buildFeatures(account: any, overrides?: Partial<NexusFeatures>): NexusFeatures {
  // Default features - can be customized based on account plan/config
  const defaultFeatures: NexusFeatures = {
    voiceCalls: true,
    videoCalls: true,
    conferencing: true,
    screenSharing: true,
    chatForwarding: true,
    voiceMessages: true,
    fileSharing: true,
    groupChats: true,
    channels: true,
    endToEndEncryption: false, // Premium feature
    messageScheduling: false, // Premium feature
    polls: true,
    reactions: true,
  }

  // Apply overrides
  return {
    ...defaultFeatures,
    ...overrides,
  }
}

/**
 * Build module availability from view permissions
 */
function buildModules(
  viewPermissions: ViewPermission[],
  overrides?: Partial<NexusModules>
): NexusModules {
  const hasAnyPermission = (names: readonly string[]) =>
    names.some((name) => viewPermissions.some((perm: ViewPermission) => perm.name === name))

  return {
    // Core modules (always available)
    chat: true,
    calls: true,
    contacts: true,
    conference: true,

    // Admin modules (permission-based)
    crm: hasAnyPermission(MODULE_PERMISSION_GROUPS.crm),
    hr: hasAnyPermission(MODULE_PERMISSION_GROUPS.hr),
    documents: hasAnyPermission(MODULE_PERMISSION_GROUPS.documents),
    timesheet: hasAnyPermission(MODULE_PERMISSION_GROUPS.timesheet),
    wallet: hasAnyPermission(MODULE_PERMISSION_GROUPS.wallet),
    billing: hasAnyPermission(MODULE_PERMISSION_GROUPS.billing),
    media: hasAnyPermission(MODULE_PERMISSION_GROUPS.media),

    // Apply overrides
    ...overrides,
  }
}

export function NexusConfigProvider({
  children,
  featureOverrides,
  moduleOverrides,
}: NexusConfigProviderProps) {
  const currentAccount = useCurrentAccount()
  const viewPermissions = useViewPermissions()

  const config = useMemo<NexusConfig>(() => {
    const features = buildFeatures(currentAccount, featureOverrides)
    const modules = buildModules(viewPermissions, moduleOverrides)
    const permissions = viewPermissions.map((perm: ViewPermission) => perm.name)

    return {
      features,
      modules,
      permissions,

      isFeatureEnabled: (feature) => features[feature] ?? false,

      isModuleEnabled: (module) => modules[module] ?? false,

      hasPermission: (permission) => permissions.includes(permission),

      hasAnyPermission: (perms) => perms.some((p) => permissions.includes(p)),

      hasAllPermissions: (perms) => perms.every((p) => permissions.includes(p)),
    }
  }, [currentAccount, viewPermissions, featureOverrides, moduleOverrides])

  return <NexusConfigContext.Provider value={config}>{children}</NexusConfigContext.Provider>
}

/**
 * Hook to access Nexus configuration
 *
 * @example
 * ```tsx
 * const { isFeatureEnabled, isModuleEnabled, hasPermission } = useNexusConfig()
 *
 * if (isFeatureEnabled('videoCalls')) {
 *   return <VideoCallButton />
 * }
 *
 * if (isModuleEnabled('crm')) {
 *   return <CrmDashboard />
 * }
 *
 * if (hasPermission('ADMIN_GROUP')) {
 *   return <AdminPanel />
 * }
 * ```
 */
export function useNexusConfig(): NexusConfig {
  const context = useContext(NexusConfigContext)

  if (!context) {
    throw new Error('useNexusConfig must be used within NexusConfigProvider')
  }

  return context
}

/**
 * Hook to check if a feature is enabled (shorthand)
 */
export function useFeature(feature: keyof NexusFeatures): boolean {
  const { isFeatureEnabled } = useNexusConfig()
  return isFeatureEnabled(feature)
}

/**
 * Hook to check if a module is enabled (shorthand)
 */
export function useModule(module: keyof NexusModules): boolean {
  const { isModuleEnabled } = useNexusConfig()
  return isModuleEnabled(module)
}

/**
 * Component to conditionally render based on feature flag
 *
 * @example
 * ```tsx
 * <FeatureFlag feature="videoCalls">
 *   <VideoCallButton />
 * </FeatureFlag>
 * ```
 */
export function FeatureFlag({
  feature,
  fallback = null,
  children,
}: {
  feature: keyof NexusFeatures
  fallback?: ReactNode
  children: ReactNode
}) {
  const isEnabled = useFeature(feature)

  if (!isEnabled) {
    return <>{fallback}</>
  }

  return <>{children}</>
}

/**
 * Component to conditionally render based on module availability
 *
 * @example
 * ```tsx
 * <ModuleGuard module="crm" fallback={<Upgrade />}>
 *   <CrmDashboard />
 * </ModuleGuard>
 * ```
 */
export function ModuleGuard({
  module,
  fallback = null,
  children,
}: {
  module: keyof NexusModules
  fallback?: ReactNode
  children: ReactNode
}) {
  const isEnabled = useModule(module)

  if (!isEnabled) {
    return <>{fallback}</>
  }

  return <>{children}</>
}
